import React, { useState, useEffect, useRef } from "react";

// Components
import Breadcrumbs from "../../../components/Breadcrumbs";
import AdjustWhModal from "../../../components/Modal/AdjustWhModal";
import Pagination from '../../../components/Footer/Pagination';

// Utils
import { getDataWithComp, getDataWithJoin, getAlert, formatCurrency, parseCurrency } from "../../../../utils/SamuiUtils";

import {
  formatDateAndTime,
  formatUTCDateAndTime,
} from "../../../../utils/DateUtils";

import {
  ExportHistoryStock,
  ExportInventories,
  ExportInventoriesWarehouse
} from "../../../../utils/ExportUtils";

import { manageWhItemData } from "../../../../utils/WarehouseUtils";
import ItemModal from "../../../components/Modal/ItemModal";

function Form({ name }) {
  const [activeTab, setActiveTab] = useState(2);
  const [formItemList, setFormItemList] = useState([]);
  const [formItemOnHand, setFormItemOnHand] = useState([]);
  const [formItemHistory, setFormItemHistory] = useState([]);
  const [formItemOnHandCaseTab, setFormItemOnHandCaseTab] = useState([]);
  const [whDataList, setWhDataList] = useState([]);
  const [itemGroupOptions, setItemGroupOptions] = useState([]);

  // ข้อมูลตั้งต้น
  const [itemId, setItemId] = useState(null);
  const [warehouseId, setWarehouseId] = useState(null);

  // สำหรับ Freeze หน้าจอตอนทำรายการ
  const [isLoading, setIsLoading] = useState(false);

  // Modal
  const [showWhModal, setShowWhModal] = useState(false);
  const handleWhShow = () => setShowWhModal(true);
  const handleWhClose = async () => {
    setShowWhModal(false);
    await initialize();
    await handleItemSearch(searchItemTerm, filterOption);
    await onRowSelectOnHand(itemId, warehouseId);
  };

  // Refs สำหรับ scroll
  const rowRefs = useRef([]);
  const pendingScrollItemIdRef = useRef(null);
  const pendingScrollWhIdRef = useRef(null);
  const currentItemIdRef = useRef(null);
  const shouldSelectFirstRowRef = useRef(true);

  const handleADJClose = async (updatedItemId, updatedWarehouseId) => {
    console.debug("handleADJClose called", { updatedItemId, updatedWarehouseId });
    setShowWhModal(false);
    
    // ถ้ามี updatedItemId และ updatedWarehouseId ให้รีเฟรชข้อมูลและ scroll
    if (updatedItemId && updatedWarehouseId) {
      // เก็บ itemId ไว้ก่อนรีเฟรช
      currentItemIdRef.current = updatedItemId;
      shouldSelectFirstRowRef.current = false;
      
      // เก็บข้อมูลไว้ใน ref เพื่อให้ useEffect จัดการ
      pendingScrollItemIdRef.current = updatedItemId;
      pendingScrollWhIdRef.current = updatedWarehouseId === "all" ? "all" : parseCurrency(updatedWarehouseId, 10);
      
      console.debug("handleADJClose: Setting refs", { 
        itemId: pendingScrollItemIdRef.current, 
        whId: pendingScrollWhIdRef.current 
      });
      
      // เลือกคลังที่ปรับปรุงใน tab ด้านล่าง
      await onChangeWarehouse(pendingScrollWhIdRef.current);
      
      // รีเฟรชข้อมูลสินค้า
      await handleItemSearch(searchItemTerm, filterOption);
    } else {
      // ถ้าไม่มีข้อมูล ให้รีเฟรชข้อมูลปกติ
      console.debug("handleADJClose: No itemId or warehouseId, refreshing normally");
      await handleItemSearch(searchItemTerm, filterOption);
    }
  };

  // useEffect สำหรับจัดการ scroll ไปที่รายการสินค้าที่ปรับปรุง
  useEffect(() => {
    if (pendingScrollItemIdRef.current && pendingScrollWhIdRef.current && formItemList.length > 0) {
      const updatedItemId = pendingScrollItemIdRef.current;
      const whIdToSelect = pendingScrollWhIdRef.current;
      
      console.debug("useEffect scroll: Processing", { 
        updatedItemId, 
        whIdToSelect, 
        formItemListLength: formItemList.length 
      });
      
      // หา index ของรายการสินค้าที่ปรับปรุง
      const itemIndex = formItemList.findIndex(item => item.Item_Id === updatedItemId);
      
      console.debug("useEffect scroll: Found index", { itemIndex });
      
      if (itemIndex >= 0) {
        // เลือกรายการสินค้าที่ปรับปรุง
        setItemId(updatedItemId);
        currentItemIdRef.current = updatedItemId;
        
        // รีเฟรชประวัติสินค้า
        onRowSelectOnHand(updatedItemId, whIdToSelect);
        
        // Scroll ไปที่รายการสินค้าที่ปรับปรุง (รอให้ DOM อัปเดต)
        setTimeout(() => {
          if (rowRefs.current[itemIndex]) {
            console.debug("useEffect scroll: Scrolling to item", { itemIndex });
            rowRefs.current[itemIndex].scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          } else {
            console.debug("useEffect scroll: rowRefs not found", { itemIndex, rowRefsLength: rowRefs.current.length });
          }
        }, 300);
      } else {
        // ถ้าหาไม่เจอ ให้รีเฟรชประวัติสินค้า
        console.debug("useEffect scroll: Item not found, refreshing history");
        onRowSelectOnHand(updatedItemId, whIdToSelect);
      }
      
      // ล้าง ref
      pendingScrollItemIdRef.current = null;
      pendingScrollWhIdRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formItemList]);

  useEffect(() => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear() + 543;
    const currentMonth = (currentDate.getMonth() + 1).toString().padStart(2, "0");

    setYear(currentYear.toString());
    setMonth(currentMonth.toString());

    initialize();
  }, []);

  const fetchItemGroups = async (items) => {
    if (!items || items.length === 0) {
      setItemGroupOptions([]);
      return;
    }

    const uniqueItemIds = Array.from(
      new Set(
        items
          .map((item) => item.Item_Id)
          .filter((itemId) => itemId !== undefined && itemId !== null)
      )
    );

    if (uniqueItemIds.length === 0) {
      setItemGroupOptions([]);
      return;
    }

    try {
      const compId = window.localStorage.getItem("company");
      const idList = uniqueItemIds.join(",");
      let whereClause = `WHERE i.Item_Id IN (${idList})`;

      if (compId) {
        whereClause += ` AND i.Comp_Id = ${compId}`;
      }

      whereClause += ` AND g.Group_Status = 'Y' ORDER BY g.Group_Name ASC`;

      const groups = await getDataWithJoin(
        "DISTINCT g.Group_Id, g.Group_Name",
        "Tb_Set_ItmGroup AS g",
        "INNER JOIN Tb_Set_Item AS i ON g.Group_Id = i.Item_Group",
        whereClause
      );

      if (groups && groups.length > 0) {
        setItemGroupOptions(groups);
      } else {
        setItemGroupOptions([]);
      }
    } catch (error) {
      console.error("Failed to load item groups:", error);
      getAlert("FAILED", "ไม่สามารถโหลดหมวดหมู่สินค้าได้");
      setItemGroupOptions([]);
    }
  };

  const initialize = async () => {
    try {
      // ค้นหาข้อมูลตั้งต้นจาก Tb_Set_Item
      const itemList = await getDataWithComp(
        "Tb_Set_Item",
        `AND Item_Status = 'Y' ORDER BY Item_Code ASC`
      );
      if (itemList && itemList.length > 0) {
        setFormItemList(itemList);
        await fetchItemGroups(itemList);
      } else {
        setFormItemList([]);
        setItemGroupOptions([]);
      }

      // ค้นหาข้อมูลตั้งต้นจาก Tb_Set_WH
      const whDataList = await getDataWithComp(
        "Tb_Set_WH",
        `AND WH_Status = 'Y' ORDER BY Flag_Vansale, WH_Name ASC`
      );
      if (whDataList && whDataList.length > 0) {
        setWhDataList(whDataList);
      }

      await loadLocalStorageData();
    } catch (error) {
      getAlert("FAILED", error.message);
    }
  };

  const loadLocalStorageData = async () => {
    const itemSearch = localStorage.getItem("stc-item-search");
    const itemOption = localStorage.getItem("stc-item-option");
    const itemId = localStorage.getItem("stc-item-id");
    const itemWH = localStorage.getItem("stc-item-wh");
    const activeTab = localStorage.getItem("stc-active-tab");
    const selectedWarehouse = localStorage.getItem("stc-select-warehouse");
    const year = localStorage.getItem("stc-year");
    const month = localStorage.getItem("stc-month");
    if (itemSearch) {
      setSearchItemTerm(itemSearch);
    }
    if (itemOption) {
      setFilterOption(itemOption);
    }
    if (itemSearch || itemOption) {
      await handleItemSearch(itemSearch, itemOption);
    }
    if (itemId && itemWH) {
      const parsedItemId = parseCurrency(itemId, 10);
      const parsedItemWH = parseCurrency(itemWH, 10);
      setItemId(parsedItemId);
      await onRowSelectOnHand(parsedItemId, parsedItemWH);
    }
    if (activeTab) {
      setActiveTab(parseCurrency(activeTab, 10));
    }
    if (selectedWarehouse) {
      setWarehouseId(parseCurrency(selectedWarehouse, 10));
    }
    if (year) {
      setYear(year);
    }
    if (month) {
      setMonth(month);
    }
    if (year || month) {
      await handleHistorySearch(year, month, itemId, selectedWarehouse);
    }
  };

  // SET ITEM_ONHAND
  const onRowSelectOnHand = async (itemId, whId) => {
    try {
      setItemId(itemId);
      setFormItemOnHand([]);

      // ค้นหาที่ View : API_1101_WH_ITEM_ONHAND Where Item_Id = ตามตารางซ้าย AND Comp_Id = ล็อกอิน Order By WHName
      const itemOnHand = await getDataWithComp(
        "API_1101_WH_ITEM_ONHAND",
        `AND Item_Id = ${itemId} ORDER BY WH_Name ASC`
      );

      if (itemOnHand && itemOnHand.length > 0) {
        setFormItemOnHand(itemOnHand); // ลบค่าทั้งหมดที่มีอยู่ก่อน
      }
    } catch (error) {
      getAlert("FAILED", error.message);
    } finally {
      await onRowSelectHistory(itemId, whId);
      await onRowSelectOnHandCaseTab(whId);
    }
  };

  // API_1201_WH_ITEM_STC (ประวัติคลังสินค้า)
  const onRowSelectHistory = async (itemId, whId) => {
    try {
      setFormItemHistory([]);

      const itemHistory = await getDataWithComp(
        "API_1201_WH_ITEM_STC",
        `AND Item_Id = ${itemId} AND WH_Id = ${whId} ORDER BY STC_SEQ DESC`
      );

      if (itemHistory && itemHistory.length > 0) {
        setFormItemHistory(itemHistory);
      }
    } catch (error) {
      getAlert("FAILED", error.message);
      setFormItemHistory([]);
    }
  };

  // API_1101_WH_ITEM_ONHAND (สินค้าคงคลัง)
  const onRowSelectOnHandCaseTab = async (whId) => {
    try {
      setFormItemOnHandCaseTab([]);

      const itemOnHandCaseTab = await getDataWithComp(
        "API_1101_WH_ITEM_ONHAND",
        `AND WH_Id = ${whId} ORDER BY Item_Code ASC`
      );

      if (itemOnHandCaseTab && itemOnHandCaseTab.length > 0) {
        setFormItemOnHandCaseTab(itemOnHandCaseTab);
      }
    } catch (error) {
      getAlert("FAILED", error.message);
      setFormItemOnHandCaseTab([]);
    }
  };

  // ตอนเลือกคลัง
  const onChangeWarehouse = async (id) => {
    try {
      setWarehouseId(id);
      onRowSelectOnHand(itemId, id);
      onRowSelectOnHandCaseTab(id);
      handleHistorySearch(year, month, itemId, id);
    } catch (error) {
      getAlert("FAILED", error.message);
    }
  };

  // SET ITEM
  const [showItemModal, setShowItemModal] = useState(false);
  const handleItemShow = () => setShowItemModal(true);
  const handleItemClose = () => setShowItemModal(false);
  const handleBeginWarehouse = async (itemsSelected) => {
    try {
      // ปิด Modal
      setShowItemModal(false);

      // สำหรับ Freeze หน้าจอตอนทำรายการ
      setIsLoading(true);

      if (warehouseId === null) {
        setIsLoading(false);
        return getAlert("FAILED", "กรุณาเลือกคลัง");
      }

      const uniqueItems = Array.from(
        new Set(itemsSelected.map(item => item.Item_Id))
      ).map(id => itemsSelected.find(item => item.Item_Id === id));

      // ใช้ Promise.all แทน for loop
      await Promise.all(uniqueItems.map(item =>
        manageWhItemData(
          item.Item_Id, // Item ไอดีของสิ่งนั้นๆ
          item.Item_Code, // Item โค้ดของสิ่งนั้นๆ
          item.Item_Name, // ชื่อ Item นั้นๆ
          parseCurrency(warehouseId), // WarehouseID ของไอเทม
          "BG", // ประเภท เช่น IN/OUT/AI/AO/TI/TO
          parseCurrency(0, 10), // ค่าจำนวนที่กรอกจากหน้าจอ
          null, // ไอดีเอกสาร
          "เริ่มต้นคลัง", // เลขเอกสาร
          "เริ่มต้นคลัง", // เลขเอกสารอ้างอิง
          null // หมายเหตุ
        )
      ));

      handleItemClose();
      await initialize();
      await onRowSelectHistory(itemId, warehouseId);
    } catch (error) {
      getAlert("FAILED", error);
    } finally {
      setIsLoading(false);
    }
  };

  // ใช้สำหรับค้นหาที่ formItemList (พี่แบงค์แจ้งมาให้ Fetch Realtime)
  const [searchItemTerm, setSearchItemTerm] = useState("");
  const [filterOption, setFilterOption] = useState("0");

  // ทำให้มันทีผลแมร่งเลย ไม่ต้องกดแว่นขยาย
  useEffect(() => {
    handleItemSearch(searchItemTerm, filterOption);
  }, [searchItemTerm, filterOption]);

  useEffect(() => {
    rowRefs.current = [];
  }, [formItemList]);

  const handleItemSearch = async (searchItemTerm, filterOption) => {
    // ทำให้ สินค้าทั้งหมด กระพริบ 
    setFormItemList([]);

    let query = `AND Item_Status = 'Y' `;

    if (searchItemTerm) {
      query += `AND (Item_Code LIKE '%${searchItemTerm}%' OR Item_Name LIKE '%${searchItemTerm}%' OR Item_Short_Name LIKE '%${searchItemTerm}%') `;
    }

    if (filterOption && filterOption !== "0") {
      const groupId = parseInt(filterOption, 10);
      if (!Number.isNaN(groupId)) {
        query += `AND Item_Group = ${groupId} `;
      }
    }

    query += `ORDER BY Item_Code ASC`;

    const itemList = await getDataWithComp("Tb_Set_Item", query, {
      fields: [
        "Item_Id",
        "Item_Code",
        "Item_Name",
        "Item_Unit_ST",
        "Item_Group",
        "Item_Status",
        "Reft_ItemOnhand"
      ]
    });
    if (itemList && itemList.length > 0) {
      setFormItemList(itemList);
      if (
        (!searchItemTerm || searchItemTerm.trim() === "") &&
        (!filterOption || filterOption === "0")
      ) {
        await fetchItemGroups(itemList);
      }
    } else {
      setFormItemList([]);
      if (
        (!searchItemTerm || searchItemTerm.trim() === "") &&
        (!filterOption || filterOption === "0")
      ) {
        setItemGroupOptions([]);
      }
    }
  };

  // ใช้สำหรับค้นหาที่ formItemHistory (พี่แบงค์แจ้งมาให้ Fetch Realtime)
  // const [searchHistoryTerm, setSearchHistoryTerm] = useState('');
  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");
  const handleHistorySearch = async (year, month, itemId, warehouseId) => {
    let query = `AND Item_Id = ${itemId} AND WH_Id = ${warehouseId} `;

    let yearInAD = "";
    if (year) {
      yearInAD = (parseCurrency(year) - 543).toString();
    }

    if (yearInAD) {
      query += `AND YEAR(STC_Date) = ${yearInAD} `;
    }

    if (month) {
      query += `AND MONTH(STC_Date) = ${parseCurrency(month)} `;
    }

    query += `ORDER BY STC_SEQ DESC`;

    const itemHistory = await getDataWithComp("API_1201_WH_ITEM_STC", query);

    if (itemHistory && itemHistory.length > 0) {
      setFormItemHistory(itemHistory);
    } else {
      setFormItemHistory([]);
    }
  };

  // Pagination state for history table
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // Calculate pagination indices
  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(
    startIndex + itemsPerPage - 1,
    formItemHistory.length
  );

  // Handler for page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <>
      <Breadcrumbs
        page={"คลังสินค้า"}
        items={[
          { name: "คลังสินค้า", url: "/Warehouse" },
          { name: name, url: "/warehouse-stock" },
          { name: "สร้าง" + name, url: "#" },
        ]}
      />

      {/* Loading Spinner */}
      {isLoading && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50 d-flex justify-content-center align-items-center"
          style={{ zIndex: 9999 }}
        >
          <div className="spinner-border text-light" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      <div style={{ maxHeight: "85vh", overflowY: "auto" }}>
        <div className="container-fluid my-2">
          <div className="row gy-2 align-items-center">
            <div className="col-12 col-lg-4">
              <div className="d-flex align-items-center">
                <h5 className="text-nowrap card-title mb-0 me-3">
                  ค้นหาสินค้า
                </h5>
                <input
                  type="text"
                  className="form-control"
                  value={searchItemTerm}
                  onChange={(e) => {
                    setSearchItemTerm(e.target.value);
                    localStorage.setItem("stc-item-search", e.target.value);
                  }}
                />
              </div>
            </div>
            <div className="col-12 col-lg-3">
              <div className="d-flex align-items-center">
                <h5 className="text-nowrap card-title mb-0 me-3">
                  หมวดหมู่สินค้า
                </h5>
                <select
                  className="form-select form-control"
                  value={filterOption || "0"}
                  onChange={(e) => {
                    setFilterOption(e.target.value);
                    localStorage.setItem("stc-item-option", e.target.value);
                  }}
                  disabled={itemGroupOptions.length === 0}
                >
                  <option value="0">ทั้งหมด</option>
                  {itemGroupOptions.map((group) => (
                    <option key={group.Group_Id} value={String(group.Group_Id)}>
                      {group.Group_Name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="col-12 col-lg-5">
              <div className="d-flex flex-wrap justify-content-lg-end gap-2">
                <button
                  className="btn text-white"
                  style={{ backgroundColor: "#F17F25", borderRadius: "2rem" }}
                  onClick={async () => {
                    setIsLoading(true);
                    await ExportInventories(formItemOnHand);
                    setIsLoading(false);
                  }}
                >
                  <i className="fa fa-file-excel me-1" aria-hidden="true"></i>{" "}
                  Export สินค้ายอดคงเหลือ
                </button>
                <button
                  className="btn text-white"
                  style={{ backgroundColor: "#F17F25", borderRadius: "2rem" }}
                  onClick={async () => {
                    setIsLoading(true);
                    await ExportInventoriesWarehouse(formItemOnHandCaseTab);
                    setIsLoading(false);
                  }}
                >
                  <i className="fa fa-warehouse me-1" aria-hidden="true"></i>{" "}
                  Export สินค้าแยกตามคลัง
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="row mt-1">
          <div className="col-6">
            <div className="col-12">
              <div className="card">
                <h5 className="card-title mt-3 me-4 text-center">
                  สินค้าทั้งหมด
                </h5>
                <div className="card-body">
                  <div
                    className="table-responsive"
                    style={{ maxHeight: "350px", overflowY: "auto" }}
                  >
                    <table
                      id="basic-datatables"
                      className="table table-striped table-hover"
                    >
                      <thead className="thead-dark">
                        <tr>
                          <th className="text-center" style={{ width: "1%" }}>
                            #
                          </th>
                          <th className="text-center" style={{ width: "17%" }}>
                            รหัสสินค้า
                          </th>
                          <th className="text-center" style={{ width: "42%" }}>
                            ชื่อสินค้า
                          </th>
                          <th className="text-center" style={{ width: "10%" }}>
                            หน่วย
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {formItemList.length > 0 ? (
                          formItemList.map((item, index) => (
                            <tr
                              key={item.Item_Id}
                              ref={(el) => (rowRefs.current[index] = el)}
                              onClick={() => {
                                onRowSelectOnHand(item.Item_Id, warehouseId);
                                localStorage.setItem(
                                  "stc-item-id",
                                  item.Item_Id
                                );
                                localStorage.setItem(
                                  "stc-item-wh",
                                  warehouseId
                                );
                              }}
                              style={{
                                cursor: "pointer",
                                backgroundColor:
                                  itemId === item.Item_Id
                                    ? "rgb(105, 188, 216)"
                                    : "transparent",
                                transition: "background-color 0.3s ease",
                              }}
                            >
                              <td className="text-center">{index + 1}</td>
                              <td className="text-left">{item.Item_Code}</td>
                              <td className="text-left">{item.Item_Name}</td>
                              <td className="text-center">
                                {item.Item_Unit_ST}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={7}>
                              <center>
                                <h5>ไม่พบข้อมูล</h5>
                              </center>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-6">
            <div className="col-12">
              <div className="card">
                <h5 className="card-title mt-3 me-4 text-center">
                  สินค้าแยกตามคลัง
                </h5>
                <div className="card-body">
                  <div className="table-responsive">
                    <table
                      id="basic-datatables"
                      className="table table-striped table-hover"
                    >
                      <thead className="thead-dark">
                        <tr>
                          <th className="text-center" style={{ width: "15%" }}>
                            รหัสสินค้า
                          </th>
                          <th className="text-center" style={{ width: "30%" }}>
                            ชื่อสินค้า
                          </th>
                          <th className="text-center" style={{ width: "10%" }}>
                            คงเหลือ
                          </th>
                          <th className="text-center" style={{ width: "25%" }}>
                            คลังสินค้า
                          </th>
                          <th className="text-center" style={{ width: "20%" }}>
                            รายการล่าสุด
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {formItemOnHand.length > 0 ? (
                          formItemOnHand.map((item, index) => (
                            <tr key={item.Item_Id || index + 1}>
                              <td className="text-center">{item.Item_Code}</td>
                              <td className="text-left">{item.Item_Name}</td>
                              <td className="text-end">
                                {formatCurrency(item.Item_Onhand)}
                              </td>
                              <td className="text-center">{item.WH_Name}</td>
                              <td className="text-center">
                                {item.Last_STC_Date
                                  ? formatDateAndTime(item.Last_STC_Date)
                                  : ""}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={6}>
                              <center>
                                <h5>ไม่พบข้อมูล</h5>
                              </center>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <hr />
        <div className="row mt-2">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <ul className="nav nav-tabs">
                  <li className="nav-item">
                    <button
                      style={{ cursor: "pointer", color: "#E13F57" }}
                      className={`nav-link ${activeTab === 1 ? "active" : ""}`}
                      onClick={() => {
                        setActiveTab(1);
                        localStorage.setItem("stc-active-tab", 1);
                      }}
                    >
                      สินค้าแยกตามคลัง
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      style={{ cursor: "pointer", color: "#E13F57" }}
                      className={`nav-link ${activeTab === 2 ? "active" : ""}`}
                      onClick={() => {
                        setActiveTab(2);
                        localStorage.setItem("stc-active-tab", 2);
                      }}
                    >
                      ประวัติสินค้าเคลื่อนไหว
                    </button>
                  </li>
                </ul>
              </div>
              <div className="tab-content mt-3">
                <div
                  className={`tab-pane fade ${activeTab === 1 ? "show active" : ""
                    }`}
                  role="tabpanel"
                >
                  <div className="col-12">
                    <div className="card">
                      <div className="card-header d-flex justify-content-between align-items-center">
                        <div className="col-4 d-flex">
                          <h4 className="card-title mb-0 me-2">เลือกคลัง</h4>
                          <select
                            style={{ width: "200px" }}
                            className="form-select"
                            value={warehouseId}
                            onChange={(e) => {
                              onChangeWarehouse(e.target.value);
                              localStorage.setItem(
                                "stc-select-warehouse",
                                e.target.value
                              );
                            }}
                          >
                            <option value={""}>กรุณาเลือกคลัง</option>
                            {whDataList.map((ware) => (
                              <option key={ware.WH_Id} value={ware.WH_Id}>
                                {ware.WH_Name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-4 d-flex"></div>
                        <div className="col-4 d-flex"></div>
                      </div>
                      <div className="card-body">
                        <div className="table-responsive">
                          <table
                            id="basic-datatables"
                            className="table table-striped table-hover"
                          >
                            <thead className="thead-dark">
                              <tr>
                                <th
                                  className="text-center"
                                  style={{ width: "10%" }}
                                >
                                  รหัสสินค้า
                                </th>
                                <th
                                  className="text-center"
                                  style={{ width: "60%" }}
                                >
                                  ชื่อสินค้า
                                </th>
                                <th
                                  className="text-center"
                                  style={{ width: "6%" }}
                                >
                                  คงเหลือ
                                </th>
                                <th
                                  className="text-center"
                                  style={{ width: "12%" }}
                                >
                                  คลังสินค้า
                                </th>
                                <th
                                  className="text-center"
                                  style={{ width: "12%" }}
                                >
                                  วันที่ทำรายการล่าสุด
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {formItemOnHandCaseTab.length > 0 ? (
                                formItemOnHandCaseTab.map((item, index) => (
                                  <tr key={item.Item_Id || index + 1}>
                                    <td className="text-center">
                                      {item.Item_Code}
                                    </td>
                                    <td className="text-left">
                                      {item.Item_Name}
                                    </td>
                                    <td className="text-end">
                                      {formatCurrency(item.Item_Onhand)}
                                    </td>
                                    <td className="text-center">
                                      {item.WH_Name}
                                    </td>
                                    <td className="text-center">
                                      {item.Last_STC_Date
                                        ? formatDateAndTime(item.Last_STC_Date)
                                        : ""}
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan={10}>
                                    <center>
                                      <h5>ไม่พบข้อมูล</h5>
                                    </center>
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  className={`tab-pane fade ${activeTab === 2 ? "show active" : ""
                    }`}
                  role="tabpanel"
                >
                  <div className="col-12">
                    <div className="card">
                      <div className="card-header">
                        <div className="container-fluid">
                          <div className="d-flex flex-row">
                            <div className="col-3 d-flex">
                              <h4 className="card-title mb-0 me-2">
                                เลือกคลัง
                              </h4>
                              <select
                                style={{ width: "200px" }}
                                className="form-select"
                                value={warehouseId}
                                onChange={(e) => {
                                  onChangeWarehouse(e.target.value);
                                  localStorage.setItem(
                                    "stc-select-warehouse",
                                    e.target.value
                                  );
                                }}
                              >
                                <option value={""}>กรุณาเลือกคลัง</option>
                                {whDataList.map((ware) => (
                                  <option key={ware.WH_Id} value={ware.WH_Id}>
                                    {ware.WH_Name}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="col-3 d-flex">
                              <label className="fw-bold">ปี : </label>
                              <input
                                type="text"
                                className="form-control mx-1"
                                value={year}
                                onChange={async (e) => {
                                  setYear(e.target.value);
                                  localStorage.setItem(
                                    "stc-year",
                                    e.target.value
                                  );

                                  await handleHistorySearch(
                                    e.target.value,
                                    month,
                                    itemId,
                                    warehouseId
                                  )
                                }}
                              />
                              <select
                                className="form-select"
                                value={month}
                                onChange={async (e) => {
                                  setMonth(e.target.value);
                                  localStorage.setItem(
                                    "stc-month",
                                    e.target.value
                                  );

                                  await handleHistorySearch(
                                    year,
                                    e.target.value,
                                    itemId,
                                    warehouseId
                                  )
                                }}
                              >
                                <option value="">กรุณาระบุเดือน</option>
                                <option value="01">มกราคม</option>
                                <option value="02">กุมภาพันธ์ </option>
                                <option value="03">มีนาคม</option>
                                <option value="04">เมษายน</option>
                                <option value="05">พฤษภาคม</option>
                                <option value="06">มิถุนายน</option>
                                <option value="07">กรกฎาคม</option>
                                <option value="08">สิงหาคม</option>
                                <option value="09">กันยายน</option>
                                <option value="10">ตุลาคม</option>
                                <option value="11">พฤศจิกายน</option>
                                <option value="12">ธันวาคม</option>
                              </select>
                              {/* <button
                                className="btn btn-outline-secondary"
                                onClick={() =>
                                  handleHistorySearch(
                                    year,
                                    month,
                                    itemId,
                                    warehouseId
                                  )
                                }
                              >
                                <i className="fas fa-search"></i>
                              </button> */}
                            </div>
                            <div className="col-2"></div>
                            <div className="col-2 d-flex justify-content-end">
                              <button
                                className="btn btn-lg text-white"
                                style={{ backgroundColor: "#F17F25", borderRadius: "2rem" }}
                                disabled={!year || !month}
                                onClick={async () => {
                                  setIsLoading(true);
                                  await ExportHistoryStock(formItemHistory, year, month);
                                  setIsLoading(false);
                                }}
                              >
                                <i className="fa fa-file-excel me-1" aria-hidden="true"></i>{" "}
                                สินค้าเคลื่อนไหว
                              </button>
                            </div>
                            <div className="col-2 d-flex justify-content-end">
                              {formItemHistory[0] ? (
                                <button
                                  className="btn text-white btn-lg"
                                  onClick={handleWhShow}
                                  style={{
                                    backgroundColor: "#F17F25",
                                    borderRadius: "2rem"
                                  }}
                                >
                                  <i className="fa fa-edit" aria-hidden="true"></i>{" "}
                                  ปรับปรุงคลัง
                                </button>
                              ) : (
                                <button
                                  className="btn text-white btn-lg"
                                  onClick={handleItemShow}
                                  style={{
                                    backgroundColor: "#F17F25",
                                    borderRadius: "2rem"
                                  }}
                                >
                                  <i className="fa fa-plus me-1" aria-hidden="true"></i>{" "}
                                  เริ่มต้นประวัติคลัง
                                </button>
                              )}
                              <ItemModal
                                formDetailList={[]}
                                showItemModal={showItemModal}
                                handleItemClose={handleItemClose}
                                itemDataList={formItemList.filter((item) => item.Reft_ItemOnhand <= 0)}
                                onRowSelectItem={handleBeginWarehouse}
                                setItemDataList={async (items) => {
                                  // หลังจากสร้างสินค้าใหม่ ให้รีเฟรชข้อมูล
                                  if (items && items.length > 0) {
                                    await handleItemSearch(searchItemTerm, filterOption);
                                  }
                                }}
                              />
                              <AdjustWhModal
                                whDataList={whDataList}
                                showWhModal={showWhModal}
                                handleWhClose={handleWhClose}
                                handleADJClose={handleADJClose}
                                itemDetailModal={formItemHistory[0]}
                                warehouseId={warehouseId}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="card-body">
                        <div className="table-responsive">
                          <table
                            id="basic-datatables"
                            className="table table-striped table-hover"
                          >
                            <thead className="thead-dark">
                              <tr>
                                <th className="text-center" style={{ width: "1%" }}>
                                  #
                                </th>
                                <th className="text-center" style={{ width: "8%" }}>
                                  รหัสสินค้า
                                </th>
                                <th className="text-center" style={{ width: "23%" }}>
                                  ชื่อสินค้า
                                </th>
                                <th className="text-center" style={{ width: "5%" }}>
                                  ประเภท
                                </th>
                                <th className="text-center" style={{ width: "8%" }}>
                                  จำนวน
                                </th>
                                <th className="text-center" style={{ width: "7%" }}>
                                  จำนวนคงเหลือ
                                </th>
                                <th className="text-center" style={{ width: "10%" }}>
                                  วันที่ทำรายการ
                                </th>
                                <th className="text-center" style={{ width: "10%" }}>
                                  คลังสินค้า
                                </th>
                                <th className="text-center" style={{ width: "8%" }}>
                                  เลขเอกสาร
                                </th>
                                <th className="text-center" style={{ width: "10%" }}>
                                  เอกสารอ้างอิง
                                </th>
                                <th className="text-center" style={{ width: "10%" }}>
                                  หมายเหตุ
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {formItemHistory.length > 0 ? (
                                formItemHistory
                                  .slice(startIndex - 1, endIndex)
                                  .map((item, index) => (
                                    <tr key={item.STC_Id}>
                                      <td className="text-center">{startIndex + index}</td>
                                      <td className="text-center">{item.Item_Code}</td>
                                      <td className="text-left">{item.Item_Name}</td>
                                      <td className="text-center">{item.Doc_Type}</td>
                                      <td className="text-end">
                                        {(item.Doc_Type === "TO" ||
                                          item.Doc_Type === "AO" ||
                                          item.Doc_Type === "OUT")
                                          ? "-"
                                          : ""}
                                        {formatCurrency(item.STC_QTY)}
                                      </td>
                                      <td className="text-end">{formatCurrency(item.STC_Balance)}</td>
                                      <td className="text-center">{formatDateAndTime(item.STC_Date)}</td>
                                      <td className="text-center">{item.WH_Name}</td>
                                      <td className="text-center">{item.Doc_No}</td>
                                      <td className="text-center">
                                        {item.Doc_NoRef &&
                                          item.Doc_NoRef.split("ยกเลิก").map(
                                            (part, idx, array) => (
                                              <React.Fragment key={idx}>
                                                {part}
                                                {idx < array.length - 1 && (
                                                  <span
                                                    style={{
                                                      color: "red",
                                                      fontWeight: "bold",
                                                    }}
                                                  >
                                                    <strong>ยกเลิก</strong>
                                                  </span>
                                                )}
                                              </React.Fragment>
                                            )
                                          )}
                                      </td>
                                      <td className="text-center">{item.STC_Remark || "-"}</td>
                                    </tr>
                                  ))
                              ) : (
                                <tr>
                                  <td colSpan={12}>
                                    <center>
                                      <h5>ไม่พบข้อมูล</h5>
                                    </center>
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                        {/* Footer */}
                        <div className="d-flex justify-content-between align-items-center p-3">
                          {/* ตัวเลือกจำนวนแถว */}
                          <div>
                            <select
                              value={itemsPerPage}
                              onChange={(e) => {
                                setItemsPerPage(Number(e.target.value));
                                setCurrentPage(1);
                              }}
                              className="form-select"
                            >
                              <option value={10}>10</option>
                              <option value={20}>20</option>
                              <option value={30}>30</option>
                              <option value={50}>50</option>
                              <option value={100}>100</option>
                            </select>
                          </div>
                          {/* การแสดงจำนวนข้อมูล */}
                          <div>
                            <span>
                              แสดง {formItemHistory.length === 0 ? 0 : startIndex}-{formItemHistory.length === 0 ? 0 : endIndex} ของ{" "}
                              {formItemHistory.length.toLocaleString()} รายการ
                            </span>
                          </div>
                          {/* Pagination */}
                          <Pagination
                            currentPage={currentPage}
                            setCurrentPage={setCurrentPage}
                            itemsPerPage={itemsPerPage}
                            dataMasterList={formItemHistory}
                            handlePageChange={handlePageChange}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Form;
